import React, { PropsWithChildren } from 'react';
import { createRoot } from 'react-dom/client';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { Outlet, RouterProvider, createBrowserRouter } from 'react-router-dom';

import { CssBaseline, StyledEngineProvider } from '@mui/material';

import { persistor, store } from 'state/store';

import { App, AppUrlChangeListener } from 'app/App';
import { AppDispatcher } from 'app/AppDispatcher';
import { ToastProvider } from 'features/common/toasts/ToastProvider';
import { Login, LoginGate } from 'features/login/Login';
import { Terms } from 'features/login/Terms';
import { notificationsRoutes } from 'features/notifications/NotificationsRoutes';
import { userAdminRoutes } from 'features/user_admin/UserAdminRoutes';
import { PersistGate } from 'redux-persist/integration/react';
import reportWebVitals from 'src/reportWebVitals';

import { ErrorPage, ReactErrorBoundary } from './app/ErrorHandling';
import { HeapAnalytics } from './features/heapAnalytics/heapAnalyticsService';
import { SSOLogin } from './features/login/SSOLogin';
import { MapIconProvider } from './features/map/MapIconProvider';
import { CemThemeProvider } from './theme/cemTheme';

import './index.css';

function TitledLayout({
  title,
  children,
}: { title: string } & PropsWithChildren) {
  const productName = import.meta.env.VITE_PRODUCT_NAME ?? 'StreetLight';
  return (
    <>
      <Helmet>
        <title>{`${title} - ${productName}`}</title>
      </Helmet>
      {children ?? <Outlet />}
    </>
  );
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <AppDispatcher />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/app',
    element: <App />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/manage',
    element: <TitledLayout title="Manage" />,
    children: userAdminRoutes(),
    errorElement: <ErrorPage />,
  },
  {
    path: '/notifications',
    element: <TitledLayout title="Notifications" />,
    children: notificationsRoutes(),
    errorElement: <ErrorPage />,
  },
  {
    path: '/login',
    element: <LoginGate />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/directlogin',
    element: <Login />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/sso',
    element: <SSOLogin />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/terms',
    element: (
      <TitledLayout title="Terms">
        <Terms />
      </TitledLayout>
    ),
    errorElement: <ErrorPage />,
  },
]);

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <HelmetProvider>
      <Provider store={store}>
        <HeapAnalytics />
        <StyledEngineProvider injectFirst>
          <CemThemeProvider>
            <CssBaseline />
            <PersistGate loading={<div>loading...</div>} persistor={persistor}>
              <ReactErrorBoundary>
                <AppUrlChangeListener />
                <RouterProvider router={router} />
              </ReactErrorBoundary>
            </PersistGate>
          </CemThemeProvider>
        </StyledEngineProvider>
      </Provider>
      <MapIconProvider />
      <ToastProvider />
    </HelmetProvider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
